import { IoSearchOutline } from 'react-icons/io5';
import { FiDownload } from 'react-icons/fi';
import React, { useState, useEffect, useContext } from 'react';
import Button from '../../components/shared/custom-btn';
import Heading from '../../components/shared/heading';
import DateRangePicker from '../../components/shared/date-range-picker';
import { AccountsContext } from '../../components/context/account-context';
import { getUsersMinimal } from '../../services/users-service';
import { getVehiclesMinimal } from '../../services/vehicles-service';
import logo from '../../assets/images/logo.jpg';
import { CSVLink } from 'react-csv';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getDateRangeUTC, formatUTCTimestampToLocal } from '../../utils/set-date-range-utils';
import { format } from 'date-fns';
import { getClockRecords } from '../../services/clock-service';
import ClockTable from './clock-table';
import { UserContext } from '../../components/context/user-context';
import Help from './help';
import { usePageHelp } from '../../components/context/help-context';

const Clocks = () => {
    usePageHelp(Help);
    const { accountId, attributes } = useContext(AccountsContext);
    const { user } = useContext(UserContext);
    const [searchTerm, setSearchTerm] = useState('');
    const [dateRange, setDateRange] = useState({
        startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
        endDate: new Date(),
    });
    const [clockData, setClockData] = useState([]);
    const [error, setError] = useState(null);
    const [users, setUsers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRows, setSelectedRows] = useState({});
    const userId = user?.userId;

    useEffect(() => {
        const fetchGlobalData = async () => {
            if (!accountId) return;
            setLoading(true);
            try {
                const [usersRes, vehiclesRes] = await Promise.all([
                    getUsersMinimal(accountId),
                    getVehiclesMinimal(accountId),
                ]);
                if (usersRes.success) setUsers(usersRes.data.data || []);
                if (vehiclesRes.success) setVehicles(vehiclesRes.data.data || []);
            } catch (err) {
                console.error("Failed to fetch global data", err);
                setError('Failed to fetch global data');
            } finally {
                setLoading(false);
            }
        };

        fetchGlobalData();
    }, [accountId]);

    const fetchClocks = async () => {
        setLoading(true);
        try {
            const { from, to } = getDateRangeUTC(dateRange);

            const response = await getClockRecords(accountId, from, to, userId);
            console.log(response)

            if (response.success) {
                const sortedData = response.data.data.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn)); // sort latest first
                setClockData(sortedData);
                setError(null);
            } else {
                setError(response.data.message || 'Failed to fetch clock records');
            }
        } catch (err) {
            setError('An error occurred while fetching clock records');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchClocks();
    }, [accountId, dateRange, userId]);


    // Filter data for export based on selected rows
    const exportData = Object.keys(selectedRows).length > 0
        ? clockData.filter((_, index) => selectedRows[index])
        : clockData;

    const csvData = exportData.map(item => ({
        "Clock Id": item.clockId,
        "User": item.user?.name || '',
        "Clock In": formatUTCTimestampToLocal(item.clockIn, 'yyyy-MM-dd hh:mm a'),
        "Clock In Location": item.clockInLocation
            ? (() => {
                const [lat, lng] = item.clockInLocation.split(',');
                return `${parseFloat(lat).toFixed(3)}, ${parseFloat(lng).toFixed(3)}`;
            })()
            : 'N/A',
        "Clock Out": formatUTCTimestampToLocal(item.clockOut, 'yyyy-MM-dd hh:mm a'),
        "Clock Out Location": item.clockOutLocation
            ? (() => {
                const [lat, lng] = item.clockOutLocation.split(',');
                return `${parseFloat(lat).toFixed(3)}, ${parseFloat(lng).toFixed(3)}`;
            })()
            : 'N/A',
        "Duration": item.duration || '',
        "Status": item.status || ''
    }));


    const handleExportPDF = () => {
        const doc = new jsPDF();

        // Logo placement
        const logoWidth = 25;
        const logoHeight = 15;
        const pageWidth = doc.internal.pageSize.getWidth();
        const logoX = (pageWidth - logoWidth) / 2;
        const logoY = 2;

        doc.addImage(logo, 'PNG', logoX, logoY, logoWidth, logoHeight);

        // Title
        const title = 'Clock Report';
        const titleY = logoY + logoHeight + 5;
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        const titleWidth = doc.getTextWidth(title);
        const titleX = (pageWidth - titleWidth) / 2;
        doc.text(title, titleX, titleY);

        // Date range
        const start = format(dateRange.startDate, 'yyyy-MM-dd');
        const end = format(dateRange.endDate, 'yyyy-MM-dd');
        const dateRangeText = `Date Range: ${start} to ${end}`;
        const dateRangeY = titleY + 5;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        const dateRangeWidth = doc.getTextWidth(dateRangeText);
        const dateRangeX = (pageWidth - dateRangeWidth) / 2;
        doc.text(dateRangeText, dateRangeX, dateRangeY);

        // Table
        autoTable(doc, {
            head: [['Clock Id', 'User', 'Clock In', 'Clock In Location', 'Clock Out', 'Clock Out Location', 'Duration', 'Status']],
            body: exportData.map(item => [
                item.clockId,
                item.user?.name || '',
                formatUTCTimestampToLocal(item.clockIn, 'yyyy-MM-dd hh:mm a'),
                item.clockInLocation
                    ? (() => {
                        const [lat, lng] = item.clockInLocation.split(',');
                        return `${parseFloat(lat).toFixed(3)}, ${parseFloat(lng).toFixed(3)}`;
                    })()
                    : 'N/A',
                formatUTCTimestampToLocal(item.clockOut, 'yyyy-MM-dd hh:mm a'),
                item.clockOutLocation
                    ? (() => {
                        const [lat, lng] = item.clockOutLocation.split(',');
                        return `${parseFloat(lat).toFixed(3)}, ${parseFloat(lng).toFixed(3)}`;
                    })()
                    : 'N/A',
                item.duration || '',
                item.status || ''
            ]),
            startY: dateRangeY + 5
        });

        const fileName = `clock-report-${start}-to-${end}.pdf`;
        doc.save(fileName);
    };

    const isExportDisabled = Object.keys(selectedRows).length === 0;

    return (
        <div className="px-6 py-4">
            <div className='flex mb-2 items-center justify-between'>
                <div>
                    <Heading>Clock History</Heading>
                </div>
                {/* <Button
                    label='Add Fuel'
                    onClick={handleOpenAddModal}
                    className='text-white px-4 !py-1 rounded-lg bg-btnBg'
                /> */}
            </div>

            <div className='space-y-4'>
                {error && (
                    <div className="text-red-600 bg-red-100 px-4 py-2 rounded-md">
                        {error}
                    </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex gap-3 flex-wrap">
                        <div className="bg-white rounded-md px-3 flex border border-border items-center gap-2 w-44 lg:w-72 py-1">
                            <IoSearchOutline className="text-gray-500 text-lg" />
                            <input
                                type="text"
                                className="bg-transparent outline-none py-1 w-full text-sm text-gray-700"
                                placeholder="Search"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <DateRangePicker
                            onChange={setDateRange}
                            initialRange={{
                                startDate: dateRange.startDate,
                                endDate: dateRange.endDate,
                            }}
                        />
                    </div>

                    <div className="flex gap-2 items-center">
                        <Button
                            onClick={handleExportPDF}
                            className={` ${isExportDisabled ? '!cursor-not-allowed !opacity-75' : 'hover:!bg-gray-50'} flex items-center gap-1 !text-sm !px-3 !py-1.5 border rounded-md `}
                            label="PDF"
                            icon={<FiDownload className="text-gray-600" />}
                            iconPosition='left'
                            disabled={isExportDisabled}
                        />
                        <div
                            className={`flex items-center gap-1 !text-sm !px-3 !py-1.5 border rounded-md ${isExportDisabled ? 'cursor-not-allowed opacity-75' : 'hover:bg-gray-50'}`}
                        >
                            <CSVLink
                                data={csvData}
                                onClick={(event) => {
                                    if (isExportDisabled) {
                                        event.preventDefault();
                                    }
                                }}
                                filename={`Clocks-${format(dateRange.startDate, 'yyyy-MM-dd')}-to-${format(dateRange.endDate, 'yyyy-MM-dd')}.csv`}
                                className={`${isExportDisabled ? 'cursor-not-allowed' : ''} flex items-center gap-1`}
                            >
                                <FiDownload className="text-gray-600" />
                                CSV
                            </CSVLink>
                        </div>
                    </div>
                </div>

                <ClockTable
                    data={clockData}
                    setData={setClockData}
                    globalFilter={searchTerm}
                    onGlobalFilterChange={setSearchTerm}
                    fetchClocks={fetchClocks}
                    loadingData={loading}
                    accountId={accountId}
                    spendors={users}
                    vehicles={vehicles}
                    attributes={attributes}
                    setSelectedRows={setSelectedRows}
                    selectedRows={selectedRows}
                />

            </div>
        </div>
    );
};

export default Clocks;